import { createColumnHelper } from '@tanstack/react-table';
import { HeadCell, SortableHeadCell, TextCell } from 'components/tables/cells';
import { TextColumnProps } from 'components/tables/columnTypes';
import { ProductAccessStatus } from 'domain/product/enums';
import ProductAccessStatusCell from 'components/tables/cells/ProductAccessStatusCell';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import { PendingReviewCell } from '../Cells/PendingReviewCell';
import { ProductAccessButtonCell } from '../Cells/ProductAccessButtonCell';
import { getProductRegistration } from './getProductRegistration';
import { MontelAccount } from 'domain/account/interfaces';

const columnHelper = createColumnHelper<MontelAccount>();

export const getProductUserColumns = (productId?: string) => ({
	lastLogin: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor(
			(row) =>
				toDefaultDateFormatOrNotAvailable(
					getProductRegistration(row.productRegistrations, productId)
						?.lastLogin,
				),
			{
				id: 'lastLogin',
				enableSorting: true,
				sortingFn: (a, b) =>
					sortingLastLogin(
						getProductRegistration(
							a.original.productRegistrations,
							productId,
						)?.lastLogin,
						getProductRegistration(
							b.original.productRegistrations,
							productId,
						)?.lastLogin,
					),
				header: ({ header }) => (
					<SortableHeadCell
						text="Last login (UTC)"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	lastActive: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor(
			(row) =>
				toDefaultDateFormatOrNotAvailable(
					getProductRegistration(row.productRegistrations, productId)
						?.lastActivity,
				),
			{
				id: 'lastActive',
				enableSorting: true,
				sortingFn: (a, b) =>
					sortingLastLogin(
						getProductRegistration(
							a.original.productRegistrations,
							productId,
						)?.lastActivity,
						getProductRegistration(
							b.original.productRegistrations,
							productId,
						)?.lastActivity,
					),
				header: ({ header }) => (
					<SortableHeadCell
						text="Last active (UTC)"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	accessStatus: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor(
			(row) =>
				(
					getProductRegistration(row.productRegistrations, productId)
						?.accessStatus || ProductAccessStatus.Default
				).toString(),
			{
				id: 'accessStatus',
				enableSorting: true,
				filterFn: (row, id, value) => {
					if (value === undefined) return true;

					const app = getProductRegistration(
						row.original.productRegistrations,
						productId,
					);

					if (!app) return false;

					return app.accessStatus === value;
				},
				header: ({ header }) => (
					<SortableHeadCell
						text="Access status"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => {
					const app = getProductRegistration(
						info.row.original.productRegistrations,
						productId,
					);

					return (
						<ProductAccessStatusCell
							status={
								app?.accessStatus || ProductAccessStatus.Default
							}
							{...overrideProps?.cellProps}
						/>
					);
				},
				...overrideProps?.columnDefProps,
			},
		),
	dateRegistered: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor(
			(row) =>
				toDefaultDateFormatOrNotAvailable(
					getProductRegistration(row.productRegistrations, productId)
						?.registeredAt,
				),
			{
				id: 'dateRegistered',
				enableSorting: true,
				sortingFn: (a, b) => {
					const appA = getProductRegistration(
						a.original.productRegistrations,
						productId,
					);
					const appB = getProductRegistration(
						b.original.productRegistrations,
						productId,
					);

					if (!appA) return -1;
					if (!appB) return 1;

					return appA.registeredAt > appB.registeredAt ? 1 : -1;
				},
				header: ({ header }) => (
					<SortableHeadCell
						text="Registered (UTC)"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	toggleAccess: (onUserAppUpdated: () => void) =>
		columnHelper.display({
			id: 'toggleAccess',
			enableSorting: false,
			meta: { width: '120px' },
			header: () => <HeadCell text="" />,
			cell: (info) => (
				<ProductAccessButtonCell
					productRegistration={getProductRegistration(
						info.row.original.productRegistrations,
						productId,
					)}
					userId={info.row.original.id}
					onSuccess={onUserAppUpdated}
				/>
			),
		}),
	markReviewed: (
		onUserAppUpdated: () => void,
		onClickReview: (account: MontelAccount) => void,
	) =>
		columnHelper.display({
			id: 'markReviewed',
			enableSorting: false,
			meta: { width: '300px', disableRowLink: true },
			filterFn: (row, id, value) => {
				if (value === undefined) return true;

				const app = getProductRegistration(
					row.original.productRegistrations,
					productId,
				);

				if (!app) return false;

				return app.reviewed === value;
			},
			header: () => <HeadCell text="" />,
			cell: (info) => {
				const app = getProductRegistration(
					info.row.original.productRegistrations,
					productId,
				);

				return (
					<PendingReviewCell
						productRegistration={app}
						account={info.row.original}
						onSuccessfulStatusUpdate={onUserAppUpdated}
						onClickReview={onClickReview}
					/>
				);
			},
		}),
});

export const sortingLastLogin = (
	a: string | undefined,
	b: string | undefined,
) => {
	if (!a) {
		return -1;
	}
	if (!b) {
		return 1;
	}

	return a > b ? 1 : -1;
};
